/**
 * Import Progress Component for CSV Import Review
 *
 * Displays an animated progress bar during import, with success/failure states
 * and result counts on completion.
 */

import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  MinusCircle,
  Plus,
  RefreshCw01,
} from '@untitledui/icons';

import { Button } from '@/lib/base/buttons/button';
import type { CsvImport, ExecutionResult } from '@/lib/csv-import/api';
import { cx } from '@/utils/cx';

// ============================================================================
// Types
// ============================================================================

interface ImportProgressProps {
  importData: CsvImport;
  /**
   * Optional close handler. When provided, a Done/Close button renders inline
   * in the completed/failed state. Omit when the parent (e.g. wizard footer)
   * provides its own close affordance.
   */
  onClose?: () => void;
  analysisTotal: number;
}

// ============================================================================
// Constants
// ============================================================================

const COLOR_CLASSES = {
  green: {
    bg: 'bg-utility-success-50',
    icon: 'text-utility-success-600',
    count: 'text-utility-success-700',
  },
  blue: {
    bg: 'bg-utility-brand-50',
    icon: 'text-utility-brand-600',
    count: 'text-utility-brand-700',
  },
  gray: {
    bg: 'bg-bg-secondary',
    icon: 'text-tertiary',
    count: 'text-secondary',
  },
} as const;

// ============================================================================
// Subcomponents
// ============================================================================

interface ResultStatProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  count: number;
  color: 'green' | 'blue' | 'gray';
}

function ResultStat({ icon: Icon, label, count, color }: ResultStatProps) {
  const classes = COLOR_CLASSES[color];

  return (
    <div
      className={cx('flex flex-col items-center p-4 rounded-lg', classes.bg)}
    >
      <Icon className={cx('w-6 h-6 mb-2', classes.icon)} />
      <span className={cx('text-2xl font-bold', classes.count)}>{count}</span>
      <span className='text-xs text-tertiary mt-1'>{label}</span>
    </div>
  );
}

function ImportingState({
  processed,
  total,
  phase,
  analysisTotal,
}: {
  processed: number;
  total: number;
  phase?: 'pending' | 'processing' | 'linking';
  analysisTotal: number;
}) {
  const isLinking = phase === 'linking';
  // Backend total is authoritative once present (>0); otherwise seed from the
  // analysis total the client already holds. Never regresses to 0.
  const displayTotal = total > 0 ? total : analysisTotal;
  const hasUsableTotal = displayTotal > 0;
  // Cold-start window: importing but no per-row progress yet (covers progress
  // null, {0,0}, the backend init heartbeat {0,total>0}, and the latent
  // processed>0-without-usable-total case so we never show a frozen "X of 0").
  const isPreparing = !isLinking && (processed === 0 || !hasUsableTotal);
  const indeterminate = isLinking || isPreparing;
  const percentage = hasUsableTotal
    ? Math.min(100, Math.round((processed / displayTotal) * 100))
    : 0;

  let heading = 'Importing your data…';
  let ariaLabel = 'Importing data';
  let centerLabel = `${percentage}%`;
  let dashArray = `${percentage * 1.76} 176`;
  let subtext: string | null =
    `${processed.toLocaleString()} of ${displayTotal.toLocaleString()} records processed`;
  if (isLinking) {
    heading = 'Linking records…';
    ariaLabel = 'Linking records';
    centerLabel = '…';
    dashArray = '176 176';
    subtext = 'Creating associations between imported records';
  } else if (isPreparing) {
    heading = 'Preparing import…';
    ariaLabel = 'Preparing import';
    centerLabel = '';
    dashArray = '44 176';
    subtext = hasUsableTotal
      ? `0 of ${displayTotal.toLocaleString()} records`
      : null;
  }

  return (
    <div
      className='flex flex-col items-center py-8'
      role='progressbar'
      aria-label={ariaLabel}
      aria-valuenow={indeterminate ? undefined : percentage}
      aria-valuemin={indeterminate ? undefined : 0}
      aria-valuemax={indeterminate ? undefined : 100}
    >
      {/* Animated spinner */}
      <div className='relative w-16 h-16 mb-6'>
        <svg
          className='w-full h-full animate-spin'
          viewBox='0 0 64 64'
          fill='none'
          aria-hidden='true'
        >
          <circle
            className='stroke-bg-quaternary'
            cx='32'
            cy='32'
            r='28'
            strokeWidth='6'
          />
          <circle
            className='stroke-fg-brand-primary'
            cx='32'
            cy='32'
            r='28'
            strokeWidth='6'
            strokeLinecap='round'
            strokeDasharray={dashArray}
            transform='rotate(-90 32 32)'
          />
        </svg>
        <div className='absolute inset-0 flex items-center justify-center'>
          <span
            className='text-sm font-semibold text-primary'
            aria-hidden='true'
          >
            {centerLabel}
          </span>
        </div>
      </div>

      <h3 className='text-lg font-medium text-primary mb-2'>{heading}</h3>
      {subtext && <p className='text-sm text-tertiary'>{subtext}</p>}

      {/* Progress bar */}
      {!indeterminate && (
        <div className='w-full max-w-xs mt-6'>
          <div className='w-full bg-bg-quaternary rounded-full h-2 overflow-hidden'>
            <div
              className='h-full bg-fg-brand-primary transition-all duration-500 ease-out w-[var(--progress-width)]'
              style={
                { '--progress-width': `${percentage}%` } as React.CSSProperties
              }
            />
          </div>
        </div>
      )}

      <p className='text-xs text-tertiary mt-4'>
        Please wait, this may take a moment…
      </p>
    </div>
  );
}

function AnalyzingState() {
  return (
    <div
      className='flex flex-col items-center py-8'
      role='status'
      aria-label='Analyzing data'
    >
      {/* Indeterminate spinner — analyzing has no percentage progress */}
      <div className='relative w-16 h-16 mb-6'>
        <svg
          className='w-full h-full animate-spin'
          viewBox='0 0 64 64'
          fill='none'
          aria-hidden='true'
        >
          <circle
            className='stroke-bg-quaternary'
            cx='32'
            cy='32'
            r='28'
            strokeWidth='6'
          />
          <circle
            className='stroke-fg-brand-primary'
            cx='32'
            cy='32'
            r='28'
            strokeWidth='6'
            strokeLinecap='round'
            strokeDasharray='44 176'
            transform='rotate(-90 32 32)'
          />
        </svg>
      </div>
      <h3 className='text-lg font-medium text-primary mb-2'>
        Analyzing your data…
      </h3>
      <p className='text-sm text-tertiary text-center max-w-sm'>
        We&rsquo;re reviewing your file and matching columns to existing fields.
        You can safely close this and come back – it&rsquo;ll keep going in the
        background.
      </p>
    </div>
  );
}

function CompletedState({
  executionResult,
  onClose,
}: {
  executionResult: ExecutionResult;
  onClose?: () => void;
}) {
  const total =
    executionResult.created + executionResult.updated + executionResult.skipped;
  const downgradedProperties = executionResult.downgraded_properties;

  return (
    <div className='flex flex-col items-center py-8'>
      {/* Success icon */}
      <div className='w-16 h-16 rounded-full bg-utility-success-100 flex items-center justify-center mb-6'>
        <CheckCircle className='w-8 h-8 text-utility-success-600' />
      </div>

      <h3 className='text-lg font-medium text-primary mb-2'>Import complete</h3>
      <p className='text-sm text-tertiary mb-6'>
        Successfully processed {total} record{total === 1 ? '' : 's'}
      </p>

      {/* Result stats */}
      <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 w-full max-w-sm'>
        <ResultStat
          icon={Plus}
          label='Created'
          count={executionResult.created}
          color='green'
        />
        <ResultStat
          icon={RefreshCw01}
          label='Updated'
          count={executionResult.updated}
          color='blue'
        />
        <ResultStat
          icon={MinusCircle}
          label='Skipped'
          count={executionResult.skipped}
          color='gray'
        />
      </div>

      {downgradedProperties && downgradedProperties.length > 0 && (
        <div className='w-full max-w-sm mt-6 rounded-lg bg-utility-warning-50 px-4 py-3'>
          <div className='flex items-center gap-2'>
            <AlertTriangle
              className='size-4 text-utility-warning-600 shrink-0'
              aria-hidden='true'
            />
            <span className='text-sm font-medium text-utility-warning-700'>
              Some fields were imported as text
            </span>
          </div>
          <ul className='mt-2 space-y-1'>
            {downgradedProperties.map(prop => (
              <li
                key={`${prop.entity_type_name}:${prop.prop_name}`}
                className='text-xs text-utility-warning-600'
              >
                <code>{prop.prop_name}</code> was imported as text instead of
                select – it had more than 100 distinct values
              </li>
            ))}
          </ul>
        </div>
      )}

      {onClose && (
        <Button color='primary' size='lg' className='mt-8' onClick={onClose}>
          Done
        </Button>
      )}
    </div>
  );
}

function FailedState({
  errorMessage,
  onClose,
}: {
  errorMessage: string | null;
  onClose?: () => void;
}) {
  return (
    <div className='flex flex-col items-center py-8'>
      {/* Error icon */}
      <div className='w-16 h-16 rounded-full bg-utility-error-100 flex items-center justify-center mb-6'>
        <AlertCircle className='w-8 h-8 text-utility-error-600' />
      </div>

      <h3 className='text-lg font-medium text-primary mb-2'>
        We couldn't import your file
      </h3>
      <p className='text-sm text-tertiary text-center max-w-sm'>
        {errorMessage ||
          'Something went wrong while importing your file. Please try again later.'}
      </p>

      {onClose && (
        <Button color='secondary' size='lg' className='mt-6' onClick={onClose}>
          Close
        </Button>
      )}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function ImportProgress({
  importData,
  onClose,
  analysisTotal,
}: ImportProgressProps) {
  const { status, progress, execution_result, error_message } = importData;

  // Importing state
  if (status === 'importing') {
    return (
      <ImportingState
        processed={progress?.processed || 0}
        total={progress?.total || 0}
        phase={progress?.phase}
        analysisTotal={analysisTotal}
      />
    );
  }

  // Analyzing state (backend is parsing + suggesting mappings)
  if (status === 'analyzing') {
    return <AnalyzingState />;
  }

  // Completed state
  if (status === 'completed' && execution_result) {
    return (
      <CompletedState executionResult={execution_result} onClose={onClose} />
    );
  }

  // Failed state
  if (status === 'failed') {
    return <FailedState errorMessage={error_message} onClose={onClose} />;
  }

  // Default/unknown state
  return (
    <div className='flex flex-col items-center py-8'>
      <p className='text-sm text-tertiary'>
        We&rsquo;re processing your import&hellip;
      </p>
    </div>
  );
}
